import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { TableRow } from "../../types/types.table";
import { ToastService } from "../../service/toast.service";
import { useRowValidation } from "./useRowValidation";

interface UseInlineRowEditParams {
  saveData?: (row: TableRow) => Promise<any>;
  reload?: () => Promise<void>;
  t: (key: string) => string;
  fields?: any[];
}

export function useInlineRowEdit({
  saveData,
  reload,
  t,
  fields = [],
}: UseInlineRowEditParams) {
  const [editingRowId, setEditingRowId] = useState<string | number | null>(
    null,
  );
  const [editBuffer, setEditBuffer] = useState<Record<string, any>>({});
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const firstFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null,
  );

  const { validateRow } = useRowValidation({ fields });

  useLayoutEffect(() => {
    if (editingRowId !== null) {
      requestAnimationFrame(() => {
        if (firstFieldRef.current) {
          firstFieldRef.current.focus();
          if (
            "select" in firstFieldRef.current &&
            firstFieldRef.current instanceof HTMLInputElement
          ) {
            firstFieldRef.current.select();
          }
        }
      });
    }
  }, [editingRowId]);

  const startEdit = (row: TableRow) => {
    setEditingRowId(row.id);

    const bufferWithPreservedJson = { ...row };

    fields?.forEach((field) => {
      const fieldType = field.file_type || field.type;

      if (fieldType === "json" && bufferWithPreservedJson[field.name]) {
        const value = bufferWithPreservedJson[field.name];
        if (typeof value === "object" && value !== null) {
          bufferWithPreservedJson[field.name] = JSON.stringify(value, null, 2);
        }
      }

      if (fieldType === "date" && bufferWithPreservedJson[field.name]) {
        const raw = String(bufferWithPreservedJson[field.name]);
        const d = new Date(raw);
        if (!isNaN(d.getTime())) {
          bufferWithPreservedJson[field.name] = d.toISOString().split("T")[0];
        }
      }

      if (
        (fieldType === "datetime" || fieldType === "timestamp") &&
        bufferWithPreservedJson[field.name]
      ) {
        const raw = String(bufferWithPreservedJson[field.name]);
        const d = new Date(raw);
        if (!isNaN(d.getTime())) {
          bufferWithPreservedJson[field.name] = d.toISOString().slice(0, 16);
        }
      }
    });

    setEditBuffer(bufferWithPreservedJson);
    setEditErrors({});
  };

  const cancelEdit = () => {
    setEditingRowId(null);
    setEditBuffer({});
    setEditErrors({});
  };

  const handleEditChange = (name: string, value: any) => {
    const newBuffer = {
      ...editBuffer,
      [name]: value,
    };
    setEditBuffer(newBuffer);

    const validationResult = validateRow(newBuffer);
    setEditErrors(validationResult.errors);
  };

  const processDataBeforeSave = (
    data: Record<string, any>,
  ): Record<string, any> => {
    const processed: Record<string, any> = {};

    for (const [key, value] of Object.entries(data)) {
      if (key === "id") continue;

      const field = fields.find((f) => {
        return String(f.name) === String(key) || f.name === key;
      });

      if (!field) {
        processed[key] = value;
        continue;
      }

      const fieldType = field.file_type || field.type;

      switch (fieldType) {
        case "color":
          if (value === null || value === undefined || value === "") {
            processed[key] = null;
          } else {
            let colorValue = String(value);
            if (!colorValue.startsWith("#")) {
              colorValue = "#" + colorValue;
            }
            if (/^#[0-9A-F]{6}$/i.test(colorValue)) {
              processed[key] = colorValue;
            } else {
              ToastService.error(`Invalid color format: ${colorValue}`);
            }
          }
          break;

        case "json":
          if (value === null || value === undefined || value === "") {
            processed[key] = null;
          } else if (typeof value === "string") {
            try {
              JSON.parse(value);
              processed[key] = value;
            } catch (error: any) {
              const errorMsg =
                t("invalidJson") ||
                `Invalid JSON in field ${key}: ${error.message}`;
              ToastService.error(errorMsg);
              throw new Error(errorMsg);
            }
          } else {
            processed[key] = JSON.stringify(value);
          }
          break;

        case "time":
          if (value === null || value === undefined || value === "") {
            processed[key] = null;
          } else {
            const timeStr = String(value);
            if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)) {
              processed[key] = timeStr;
            } else {
              console.warn(`Invalid time format for field ${key}: ${timeStr}`);
              processed[key] = null;
            }
          }
          break;

        case "date":
        case "Date":
          if (value === null || value === undefined || value === "") {
            processed[key] = null;
          } else {
            const dateStr = String(value);
            processed[key] = dateStr.split("T")[0];
          }
          break;

        case "datetime":
        case "timestamp":
          if (value === null || value === undefined || value === "") {
            processed[key] = null;
          } else {
            let dateStr = String(value);
            if (dateStr.includes("T") && !dateStr.includes("Z")) {
              const parts = dateStr.split(":");
              if (parts.length === 2) {
                dateStr = dateStr + ":00";
              }
            }
            processed[key] = dateStr;
          }
          break;

        case "int":
        case "integer":
        case "INT8":
          if (value === null || value === undefined || value === "") {
            continue;
          } else {
            const intValue = parseInt(String(value), 10);
            processed[key] = isNaN(intValue) ? null : intValue;
          }
          break;

        case "double":
        case "float":
        case "decimal":
          if (value === null || value === undefined || value === "") {
            processed[key] = null;
          } else {
            const floatValue = parseFloat(String(value).replace(",", "."));
            processed[key] = isNaN(floatValue) ? null : floatValue;
          }
          break;

        case "bigint":
          if (value === null || value === undefined || value === "") {
            processed[key] = null;
          } else {
            processed[key] = String(value);
          }
          break;

        case "boolean":
        case "bool":
          processed[key] =
            value === true || value === "true" || value === 1 || value === "1";
          break;

        case "multiselect":
          if (Array.isArray(value)) {
            processed[key] = value;
          } else if (typeof value === "string") {
            try {
              const parsed = JSON.parse(value);
              processed[key] = Array.isArray(parsed) ? parsed : [parsed];
            } catch {
              processed[key] = [];
            }
          } else {
            processed[key] = [];
          }
          break;

        case "password":
          if (value && value !== "••••••••") {
            processed[key] = value;
          }
          break;

        case "fk":
        case "select":
          if (
            value === null ||
            value === undefined ||
            value === "" ||
            value === "null"
          ) {
            processed[key] = null;
          } else {
            const numValue = Number(value);
            processed[key] = isNaN(numValue) ? value : numValue;
          }
          break;

        default:
          processed[key] = value;
      }
    }

    return processed;
  };

  const saveEdit = async () => {
    if (!editingRowId) return;
    if (!saveData) return;

    try {
      const validationResult = validateRow(editBuffer);

      if (!validationResult.isValid) {
        setEditErrors(validationResult.errors);
        const errorMessages = Object.values(validationResult.errors).join(", ");
        ToastService.error(errorMessages);
        return;
      }

      const processedData = processDataBeforeSave(editBuffer);

      const payload: TableRow = {
        ...processedData,
        id: editingRowId,
      };

      console.log("send to back:", JSON.stringify(payload, null, 2));
      if (payload.jsonf && typeof payload.jsonf === "string") {
        console.log("json field sent:", payload.jsonf);
      }

      await saveData(payload);

      setEditingRowId(null);
      setEditBuffer({});
      setEditErrors({});

      await reload?.();

      ToastService.success(t("rowUpdated"));

      setTimeout(() => {
        const rowElement = document.querySelector(
          `[data-row-index][data-row-type="data"]`,
        );

        if (rowElement) {
          const firstInput = rowElement.querySelector(
            'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), button:not([disabled])',
          ) as HTMLElement;

          firstInput?.focus();
        }
      }, 100);
    } catch (error: any) {
      let errorMessage = t("errorSaving") || "Error saving data";
      ToastService.error(errorMessage);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      saveEdit();
    }
    if (e.key === "Escape") {
      cancelEdit();
    }
  };

  const hasErrors = Object.keys(editErrors).length > 0;

  return {
    editingRowId,
    editBuffer,
    editErrors,
    hasErrors,
    firstFieldRef,
    setEditBuffer,
    startEdit,
    cancelEdit,
    saveEdit,
    handleKey,
    handleEditChange,
  };
}
